import { defaultTheme as theme } from '@automata/mui-theme';
import { FieldError, TextField, Typography } from '@automata/ui';
import { useFlag } from '@automata/utils/hooks';
import { TaskOutputReferenceInput } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import { Checkbox, FormControlLabel, Stack } from '@mui/material';
import isEqual from 'lodash/isEqual';
import { FC, useCallback, useEffect } from 'react';

import { deleteNodesEdges } from '../../../../features/actions';
import { updateNodesEdgesSelection } from '../../../../features/graph/graphSlice';
import { selectDataDependencyEdges } from '../../../../features/graph/selectors';
import {
  removeTaskParameter,
  unbindParameterDefinitionOfTask,
  updateTaskParameters,
} from '../../../../features/workflow/workflowSlice';
import { selectDataDependencies } from '../../../../features/workflowJsonSelectors';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { CodeTaskProperties, CodeTaskPropertiesForm } from '../../../../types';
import {
  buildModePatchForParam,
  getParamMode,
} from '../../../../utils/dataModes';
import {
  DataMode,
  TaskProperties,
  TaskPropertiesForm,
} from '../../../../zodSchemas/taskProperties';
import { ParameterType } from '../../../GraphEditorToolbars/ParametersToolbar/types';
import { useFieldData } from '../../hooks';
import { ExposingParameterChip } from '../ExposingParameterChip';
import { ExposingParameterPopover } from '../ExposingParameterPopover/ExposingParameterPopover';

interface ConnectorizableFileParamProps {
  inputParameterName: string;
  label?: string;
  mode?: DataMode;
  required?: boolean;
  taskProperties: TaskProperties | CodeTaskProperties;
  updateTaskFields: (
    formUpdate: Partial<TaskPropertiesForm | CodeTaskPropertiesForm>
  ) => void;
}

export const ConnectorizableFileParam: FC<ConnectorizableFileParamProps> = ({
  inputParameterName,
  label,
  mode,
  required,
  taskProperties,
  updateTaskFields,
}) => {
  const taskId = taskProperties.id;
  const dispatch = useAppDispatch();
  const dataDependencyEdges = useAppSelector(selectDataDependencyEdges);
  const depsForTask = useAppSelector(selectDataDependencies)[taskId];
  const workflowLevelErrorsVisible = useFlag(
    'fe.canvas-builder-workflow-errors-visible'
  );

  const { getFieldData } = useFieldData(taskProperties);
  const {
    parameterAppliedReferenceId,
    parameterReference,
    parameterAppliedValue,
  } = getFieldData(inputParameterName);

  const error =
    workflowLevelErrorsVisible && required && !parameterAppliedValue
      ? 'Field is required.'
      : null;

  const toggleDataMode = useCallback(
    (val: boolean): void => {
      const next: DataMode = !val ? DataMode.Manual : DataMode.Connector;

      updateTaskFields(buildModePatchForParam(inputParameterName, next));

      const edge = dataDependencyEdges.find(
        (e) =>
          e.target === taskId && e.targetHandle?.includes(inputParameterName)
      );

      if (edge) {
        dispatch(deleteNodesEdges({ edgeIds: [edge.id] }));
        dispatch(
          updateNodesEdgesSelection({
            nodes: { selected: [taskId], deselected: [] },
          })
        );
      }
    },
    [
      dataDependencyEdges,
      inputParameterName,
      taskId,
      dispatch,
      updateTaskFields,
    ]
  );

  useEffect(() => {
    const mode = getParamMode(taskProperties, inputParameterName);

    if (!mode) return;

    if (mode === DataMode.Connector) {
      const isParameterApplied =
        !!taskProperties.taskAppliedParameters?.[inputParameterName];
      if (isParameterApplied) {
        dispatch(
          unbindParameterDefinitionOfTask({
            fieldName: inputParameterName,
            taskId,
          })
        );
      }
    }

    const connectedRef: TaskOutputReferenceInput | null =
      (depsForTask?.[inputParameterName] as TaskOutputReferenceInput) ?? null;
    const savedConnection = taskProperties.taskParameters?.[inputParameterName];
    if (connectedRef && !isEqual(savedConnection, connectedRef)) {
      dispatch(
        updateTaskParameters({
          id: taskId,
          updates: { [inputParameterName]: connectedRef },
          isDataOutput: false,
        })
      );
    }
    if (!connectedRef && !!savedConnection) {
      dispatch(
        removeTaskParameter({ id: taskId, fieldName: inputParameterName })
      );
    }
  }, [depsForTask, inputParameterName, taskId, taskProperties, dispatch]);

  return (
    <>
      {label && (
        <Typography
          color="text.primary"
          component="label"
          pt={1}
          variant="overline"
        >
          {label}
        </Typography>
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={mode === DataMode.Connector}
            onChange={(e) => toggleDataMode(e.target.checked)}
          />
        }
        label="Input from connector"
        sx={{ color: theme.palette.grey[700] }}
      />
      {mode === DataMode.Manual && (
        <Stack gap={1} flex={1}>
          <Stack flexDirection="row" alignItems="flex-end" width="100%">
            <TextField
              disabled={true}
              error={!!error}
              placeholder="Please select a file parameter"
              value={parameterAppliedValue}
            />
            <ExposingParameterPopover
              appliedParameterId={parameterReference?.id}
              parameterName={inputParameterName}
              taskId={taskId}
              types={ParameterType.FILE}
            />
          </Stack>
          {parameterAppliedReferenceId && (
            <ExposingParameterChip
              applyParamValue={false}
              fieldName={inputParameterName}
              parameter={parameterReference}
              taskId={taskId}
            />
          )}
          <FieldError error={error} />
        </Stack>
      )}
    </>
  );
};

ConnectorizableFileParam.displayName = 'ConnectorizableFileParam';
